import {
  type ArgumentNode,
  type FieldNode,
  Kind,
  type ObjectFieldNode,
  type ObjectValueNode,
  OperationTypeNode,
  type SelectionNode,
} from 'graphql';
import { makeOperation, mapExchange, type Operation } from 'urql';

import { Storage, StorageKey, StorageKind } from '@revelio/core';
import type { ResponseCacheOptions } from '@revelio/data-access';

export type CacheBypassFlags = {
  bypassMwCache: boolean;
  bypassDbeCache: boolean;
};

const STORAGE_KEY_BY_FLAG: Record<keyof CacheBypassFlags, StorageKey> = {
  bypassMwCache: StorageKey.SkipMwCache,
  bypassDbeCache: StorageKey.SkipDbeCache,
};

export const getCacheBypassFlags = (): CacheBypassFlags => ({
  bypassMwCache: Storage.readJson(
    StorageKind.Session,
    StorageKey.SkipMwCache,
    false
  ),
  bypassDbeCache: Storage.readJson(
    StorageKind.Session,
    StorageKey.SkipDbeCache,
    false
  ),
});

export const setCacheBypassFlag = (
  flag: keyof CacheBypassFlags,
  enabled: boolean
) => {
  Storage.writeJson(StorageKind.Session, STORAGE_KEY_BY_FLAG[flag], enabled);
};

/**
 * Root query fields whose input accepts `cache_options: ResponseCacheOptions`
 * in the middleware schema, mapped to the argument holding that input.
 *
 * Hand-maintained, but a schema-conformance test (see the spec) rebuilds the
 * expected set from the GraphQL schema and fails if this map drifts — so a
 * newly added cache-capable field can't silently miss out on injection.
 */
export const CACHE_OPTIONS_ARG_BY_FIELD: Record<string, string> = {
  composition_v2: 'req',
  composition_v2_tooltip: 'req',
  composition_v3: 'req',
  pay_v2: 'req',
  posting: 'options',
  postings_v3: 'req',
  transitionsV2: 'req',
};

const getCacheBypassOptions = (
  flags: CacheBypassFlags
): ResponseCacheOptions | null => {
  const { bypassMwCache, bypassDbeCache } = flags;
  if (!bypassMwCache && !bypassDbeCache) return null;
  return {
    ...(bypassMwCache && { skip_mw_cache: true }),
    ...(bypassDbeCache && { skip_dbe_cache: true }),
  };
};

const cacheOptionsObjectValue = (
  options: ResponseCacheOptions
): ObjectValueNode => ({
  kind: Kind.OBJECT,
  fields: Object.entries(options).map(
    ([name, value]): ObjectFieldNode => ({
      kind: Kind.OBJECT_FIELD,
      name: { kind: Kind.NAME, value: name },
      value: { kind: Kind.BOOLEAN, value: Boolean(value) },
    })
  ),
});

const cacheOptionsObjectField = (
  options: ResponseCacheOptions
): ObjectFieldNode => ({
  kind: Kind.OBJECT_FIELD,
  name: { kind: Kind.NAME, value: 'cache_options' },
  value: cacheOptionsObjectValue(options),
});

type Variables = Record<string, unknown>;

/**
 * How to apply cache_options to one root field: a rewritten field node, a new
 * variables object, or both. `null` means the field needs no change (it does
 * not take cache_options, or a caller already provided one).
 */
type FieldUpdate = {
  field?: FieldNode;
  variables?: Variables;
};

const transformField = (
  field: FieldNode,
  options: ResponseCacheOptions,
  variables: Variables
): FieldUpdate | null => {
  const argName = CACHE_OPTIONS_ARG_BY_FIELD[field.name.value];
  if (argName === undefined) return null;

  const arg = field.arguments?.find((a) => a.name.value === argName);

  // No input argument in the document: add `req/options: { cache_options }`.
  if (!arg) {
    const newArg: ArgumentNode = {
      kind: Kind.ARGUMENT,
      name: { kind: Kind.NAME, value: argName },
      value: { kind: Kind.OBJECT, fields: [cacheOptionsObjectField(options)] },
    };
    return {
      field: { ...field, arguments: [...(field.arguments ?? []), newArg] },
    };
  }

  // e.g. `pay_v2(req: $req)`: merge cache_options into the variable's value.
  if (arg.value.kind === Kind.VARIABLE) {
    const varName = arg.value.name.value;
    const input = variables[varName];
    if (input && typeof input === 'object' && !('cache_options' in input)) {
      return {
        variables: {
          ...variables,
          [varName]: { ...input, cache_options: options },
        },
      };
    }
    return null;
  }

  if (arg.value.kind === Kind.OBJECT) {
    const existing = arg.value.fields.find(
      (f) => f.name.value === 'cache_options'
    );

    // e.g. `composition_v3(req: { entities: $entities, ... })`: append inline.
    if (!existing) {
      const newArg: ArgumentNode = {
        ...arg,
        value: {
          ...arg.value,
          fields: [...arg.value.fields, cacheOptionsObjectField(options)],
        },
      };
      return {
        field: {
          ...field,
          arguments: field.arguments?.map((a) => (a === arg ? newArg : a)),
        },
      };
    }

    // `cache_options: $cache_options` declared: supply the variable if unset.
    if (existing.value.kind === Kind.VARIABLE) {
      const existingVar = variables[existing.value.name.value];
      if (existingVar === undefined || existingVar === null) {
        return {
          variables: { ...variables, [existing.value.name.value]: options },
        };
      }
    }
  }

  return null;
};

const applyCacheOptions = (
  operation: Operation,
  options: ResponseCacheOptions
): Operation => {
  // A document can hold several cache-capable root fields, each potentially
  // contributing a variable change, so variables is folded across all of them.
  let variables: Variables = { ...operation.variables };
  let changed = false;

  const definitions = operation.query.definitions.map((definition) => {
    if (
      definition.kind !== Kind.OPERATION_DEFINITION ||
      definition.operation !== OperationTypeNode.QUERY
    ) {
      return definition;
    }

    const selections = definition.selectionSet.selections.map(
      (selection): SelectionNode => {
        if (selection.kind !== Kind.FIELD) return selection;

        const update = transformField(selection, options, variables);
        if (update === null) return selection;

        changed = true;
        variables = update.variables ?? variables;
        return update.field ?? selection;
      }
    );

    return {
      ...definition,
      selectionSet: { ...definition.selectionSet, selections },
    };
  });

  if (!changed) return operation;

  return makeOperation(
    operation.kind,
    {
      key: operation.key,
      query: { ...operation.query, definitions },
      variables,
    },
    operation.context
  );
};

/**
 * URQL exchange that asks the middleware to skip its response caches
 * (`skip_mw_cache` / `skip_dbe_cache`) when the corresponding admin settings
 * are enabled, by injecting `cache_options` into every query that supports it
 * (see CACHE_OPTIONS_ARG_BY_FIELD).
 *
 * With both flags off (the default) operations pass through unchanged, and a
 * `cache_options` value already provided by a caller is never overridden.
 */
export const cacheBypassExchange = (getFlags: () => CacheBypassFlags) =>
  mapExchange({
    onOperation(operation) {
      if (operation.kind !== 'query') return operation;

      const options = getCacheBypassOptions(getFlags());
      if (!options) return operation;

      return applyCacheOptions(operation, options);
    },
  });
